import { useCallback, useEffect, useRef, useState } from "react";
import {
  getModelPackAPI,
  getModelPackDownloadUrlAPI,
  publishModelPackAPI,
} from "../api/modelManagementAPI";
import { getLoggedInOwnerId } from "../../../../utils/auth";

// While a pack is building the state only changes on the server, so this polls.
// Fast while building, and NOT AT ALL otherwise: a pack is immutable once ready,
// so there is nothing a later poll could discover.
const POLL_MS = 3000;

/**
 * The deployment pack for one trained version (see 2_training_api/core/business_logics
 * /b_model_pack.py, and g_portal/docs/MODEL_BRIDGE_PLAN.md phase L0).
 *
 * Four states, and they are deliberately four rather than two:
 *   null      never published  -> offer Publish
 *   building  a build is running -> show progress, offer nothing
 *   ready     publishable artifact exists -> show it, offer Download / Rebuild
 *   failed    a build failed -> show the REASON and offer Retry
 * Collapsing "never published" into "failed" is what makes a screen say something
 * is broken when nobody has tried yet.
 */
export default function useModelPack(modelType, versionId) {
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef(null);
  // Guards a setState after unmount, and — more usefully — stops a poll that is
  // already in flight from reviving a timer the cleanup just cleared.
  const alive = useRef(true);

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const read = useCallback(
    async ({ silent } = {}) => {
      if (!modelType || !versionId) return null;
      if (!silent) setLoading(true);
      try {
        const res = await getModelPackAPI({ modelType, modelDataInfoId: versionId });
        const next = res?.data?.data ?? null;
        if (alive.current) {
          setPack(next);
          setError("");
        }
        return next;
      } catch (err) {
        if (alive.current) {
          setError(err?.response?.data?.detail || err?.message || "Could not read the deployment pack");
        }
        return null;
      } finally {
        if (alive.current && !silent) setLoading(false);
      }
    },
    [modelType, versionId]
  );

  // One scheduler, driven by the state we last saw. Re-arming from inside the poll
  // itself is how two timers end up running after a fast Publish.
  useEffect(() => {
    clearTimer();
    if (pack?.status !== "building") return undefined;
    timer.current = setTimeout(() => {
      read({ silent: true });
    }, POLL_MS);
    return clearTimer;
  }, [pack, read, clearTimer]);

  useEffect(() => {
    alive.current = true;
    read();
    return () => {
      alive.current = false;
      clearTimer();
    };
  }, [read, clearTimer]);

  const publish = useCallback(
    async ({ force = false, imgsz = 640 } = {}) => {
      const ownerId = getLoggedInOwnerId();
      if (!ownerId) {
        // Reported rather than sent: publishing is owner-only on the server, so an
        // empty ownerId is a 403 whose message would be about permissions when the
        // real problem is that this browser has no session.
        setError("You are signed out — sign in again to publish a deployment pack.");
        return null;
      }
      setPublishing(true);
      setError("");
      try {
        const res = await publishModelPackAPI({
          modelType,
          modelDataInfoId: versionId,
          ownerId,
          imgsz: String(imgsz),
          force: force ? "true" : "false",
        });
        const next = res?.data?.data ?? null;
        if (alive.current && next) setPack(next);
        return next;
      } catch (err) {
        if (alive.current) {
          setError(err?.response?.data?.detail || err?.message || "Publishing failed");
        }
        return null;
      } finally {
        if (alive.current) setPublishing(false);
      }
    },
    [modelType, versionId]
  );

  const download = useCallback(async () => {
    try {
      const res = await getModelPackDownloadUrlAPI({ modelType, modelDataInfoId: versionId });
      const url = res?.data?.data?.url;
      if (url) window.open(url, "_blank", "noopener");
      return url;
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Could not build a download link");
      return null;
    }
  }, [modelType, versionId]);

  return { pack, loading, publishing, error, publish, download, refresh: read };
}
